import * as Sentry from '@sentry/react-native'
import { consoleTransport, logger } from 'react-native-logs'
import { addLogEntry } from './log-buffer'

const levels = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
}

type LogLevel = keyof typeof levels

const config = {
    levels,
    severity: __DEV__ ? 'debug' : 'info',
    transport: consoleTransport,
    transportOptions: {
        colors: {
            debug: 'white' as const,
            info: 'blueBright' as const,
            warn: 'yellowBright' as const,
            error: 'redBright' as const,
        },
    },
    async: true,
    dateFormat: 'time' as const,
    printLevel: true,
    printDate: true,
    enabled: true,
}

// Minimum severity level for sending to Sentry
const sentrySeverity: LogLevel = 'error'

const baseLog = logger.createLogger(config)

// biome-ignore lint/suspicious/noConsole: storing original console.warn to override and filter noisy warnings
const originalConsoleWarn = console.warn

console.warn = (...args) => {
    if (
        args[0].match(
            /shadow\*" style props are deprecated|New web implementation is enabled by default|pointerEvents is deprecated|useNativeDriver` is not supported/
        )
    ) {
        return
    }

    originalConsoleWarn(...args)
}

// Store original functions
const originalDebug = baseLog.debug
const originalInfo = baseLog.info
const originalWarn = baseLog.warn
const originalError = baseLog.error

// Format args into a single string for logging and buffering
const formatArgs = (args: unknown[]) =>
    args.map(a => (a instanceof Error ? a.message : String(a))).join(' : ')

// Helper to send to Sentry based on severity
const sendToSentry = (level: LogLevel, args: unknown[]) => {
    if (args.length === 0) return

    const message = formatArgs(args)
    addLogEntry(level, message)

    if (levels[level] < levels[sentrySeverity]) {
        Sentry.addBreadcrumb({
            category: 'log',
            message,
            level: (level === 'warn' ? 'warning' : level) as Sentry.SeverityLevel,
        })
        return
    }

    const error = args.find(arg => arg instanceof Error)
    if (error) {
        Sentry.captureException(error)
    } else {
        const sentryLevel = level === 'warn' ? 'warning' : level
        Sentry.captureMessage(args.join(' : '), sentryLevel as Sentry.SeverityLevel)
    }
}

// Extend logger to send messages above threshold to Sentry
const log = {
    ...baseLog,
    debug: (...args: unknown[]) => {
        sendToSentry('debug', args)
        return originalDebug(...args)
    },
    info: (...args: unknown[]) => {
        sendToSentry('info', args)
        return originalInfo(...args)
    },
    warn: (...args: unknown[]) => {
        sendToSentry('warn', args)
        return originalWarn(...args)
    },
    error: (...args: unknown[]) => {
        sendToSentry('error', args)
        return originalError(...args)
    },
    // Log error without sending to Sentry (for cases where Sentry is handled separately)
    errorWithoutSentry: originalError,
    // Add a breadcrumb to both Sentry and the log buffer (for non-logger callers like geo-tracking)
    breadcrumb: (category: string, message: string, data?: Record<string, unknown>) => {
        addLogEntry(
            'breadcrumb',
            `[${category}] ${message}${data ? ` ${JSON.stringify(data)}` : ''}`
        )
        Sentry.addBreadcrumb({
            category,
            level: 'info',
            message,
            data,
        })
    },
} as typeof baseLog & {
    errorWithoutSentry: typeof originalError
    breadcrumb: (category: string, message: string, data?: Record<string, unknown>) => void
}

export { log }
